import { useEffect, useRef } from "react";
import { smoothScrollTo } from "../utils/smoothScroll";

/**
 * Custom hook to handle hash-based navigation on page load and hash changes
 * Specifically designed to work with lazy-loaded React components
 */
export const useHashNavigation = () => {
  const hasHandledInitialHash = useRef(false);
  const validSections = [
    "home",
    "media",
    "publications",
    "projects",
    "contact",
  ];

  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (!hash) return;

      const sectionId = hash.replace("#", "");

      // Only handle valid sections
      if (!validSections.includes(sectionId)) {
        console.warn(`Invalid section hash: ${hash}`);
        return;
      }

      let attempts = 0;
      const maxAttempts = 20; // Increased for lazy loading
      const baseDelay = 150;

      const scrollToTarget = () => {
        const target = document.querySelector(hash);
        if (target) {
          // Check if element is actually rendered and visible
          const rect = target.getBoundingClientRect();
          const isVisible = rect.height > 0 && rect.width > 0;

          // For lazy-loaded sections, also check if they have content
          const hasContent =
            target.children.length > 0 || target.textContent.trim().length > 0;

          if (isVisible && hasContent) {
            // Small delay to ensure smooth scrolling works properly
            setTimeout(() => {
              smoothScrollTo(hash, { offset: 80 });

              // Update browser history without triggering hashchange
              if (window.location.hash !== hash) {
                history.replaceState(null, null, hash);
              }
            }, 50);
            return true;
          }
        }
        return false;
      };

      const attemptScroll = () => {
        attempts++;

        if (scrollToTarget()) {
          return; // Success!
        }

        // Progressive delay increase for lazy loading
        if (attempts < maxAttempts) {
          const delay = baseDelay * Math.min(attempts * 0.5, 3);
          setTimeout(attemptScroll, delay);
        } else {
          console.warn(
            `Failed to navigate to ${hash} after ${maxAttempts} attempts`
          );
        }
      };

      // Start the scroll attempt
      attemptScroll();
    };

    // Handle initial hash on page load
    if (!hasHandledInitialHash.current) {
      hasHandledInitialHash.current = true;

      // Multiple attempts with increasing delays for lazy-loaded content
      const delays = [100, 300, 600, 1000, 1500];
      delays.forEach((delay) => {
        setTimeout(handleHashNavigation, delay);
      });
    }

    // Handle hash changes (browser back/forward, manual URL changes)
    const handleHashChange = () => {
      handleHashNavigation();
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [validSections]);

  // Return a function to manually trigger hash navigation
  const navigateToHash = (hash) => {
    if (!hash.startsWith("#")) {
      hash = "#" + hash;
    }
    window.location.hash = hash;
  };

  return { navigateToHash, validSections };
};
